import { Config } from '@src/core/Config';

export interface LoopCallbacks {
  tick(dt: number): void;
  render(alpha: number, frameDt: number): void;
}

/**
 * Fixed-timestep simulation (120 Hz) decoupled from the render rate.
 * Supports hit-stop: `freeze(ms)` halts simulation time while rendering continues.
 * Pause (`pause()` / `resume()`) freezes ticks the same way without stopping the RAF.
 */
export class GameLoop {
  private readonly step = 1 / Config.sim.tickHz;
  private accumulator = 0;
  private last = 0;
  private rafId = 0;
  private running = false;
  private paused = false;
  private freezeUntil = 0;
  private timeScale = 1;

  constructor(private readonly callbacks: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** Halt simulation ticks; rendering and presentation systems keep running. */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.accumulator = 0;
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.last = performance.now();
    this.accumulator = 0;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  get isRunning(): boolean {
    return this.running;
  }

  freeze(ms: number): void {
    const until = performance.now() + ms;
    if (until > this.freezeUntil) this.freezeUntil = until;
  }

  setTimeScale(scale: number): void {
    this.timeScale = Math.max(0, scale);
  }

  private readonly frame = (now: number): void => {
    if (!this.running) return;
    let frameDt = (now - this.last) / 1000;
    this.last = now;
    if (frameDt > 0.25) frameDt = 0.25;

    const frozen = this.paused || now < this.freezeUntil;
    if (!frozen) this.accumulator += frameDt * this.timeScale;

    let steps = 0;
    while (this.accumulator >= this.step && steps < Config.sim.maxStepsPerFrame) {
      this.callbacks.tick(this.step);
      this.accumulator -= this.step;
      steps++;
    }
    if (steps === Config.sim.maxStepsPerFrame) this.accumulator = 0;

    // Alpha is meaningless while paused; keep the last interpolated pose stable.
    this.callbacks.render(frozen ? 1 : this.accumulator / this.step, frameDt);
    this.rafId = requestAnimationFrame(this.frame);
  };
}
